import React, { useState, useRef } from 'react';
import { isBrowser } from 'react-device-detect';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from 'containers/ProjectTools/Notes/actions';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAutosizeTextarea } from 'hooks/useAutosizeTextarea';
import { useFloatingTooltip } from 'hooks/useFloatingTooltip';
import { MAX_NOTE_NAME_LENGTH } from 'utils/constants';
import { NotesOptionsButton } from './Notes.OptionsButton';

const AutosizeTextarea = (props) => {
  const { textareaRef } = useAutosizeTextarea({ enterToSubmit: true });
  return (
    <textarea
      ref={textareaRef}
      maxLength={MAX_NOTE_NAME_LENGTH}
      {...props}
    />
  );
};

const Note = ({ title, id, url, setNodeRef, attributes, listeners, style, goToNote, notesEdit, ...restProps }) => {
  // #TDOD: Refactor this. We can just pass props to Note and destructure what we need.
  // Then we can just pass the whole note object around to whatever components need them.
  const {
    imageUrls,
    image,
    originalImage,
    weddingId,
    plannerOnly,
    clientReadOnly,
    noteHtml,
    noteJson,
    categoryId,
    isShortcut,
    isOwner,
    showBrandingHeader,
    isFileUpload,
    file,
    fileViewerUrl,
    noteFileId,
  } = { ...restProps };

  const noteData = {
    id,
    weddingId,
    title,
    url,
    plannerOnly,
    clientReadOnly,
    noteHtml,
    noteJson,
    categoryId,
    isShortcut,
    showBrandingHeader,
    isFileUpload,
    file,
    fileViewerUrl,
    noteFileId,
    image,
    imageUrls,
  };
  const [IsImageUpdating, setImageUpdating] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState('');
  const imageUrl = imageUrls?.rect260x175 ? imageUrls.rect260x175.url : null;
  const [editNoteTitle, setEditNoteTitle] = useState(false);
  const [noteTitle, setNoteTitle] = useState(title);
  const cardRef = useRef();

  const dispatch = useDispatch();
  const { renameNote } = bindActionCreators(actions, dispatch);

  const notesViewOptions = useSelector((state) => state.global.notesViewOptions);

  const onChangeTitle = (e) => {
    if (e?.target?.value) {
      renameNote({ id, name: e.target.value });
      setNoteTitle(e.target.value);
    }
    setEditNoteTitle(false);
  };

  const {
    showTooltip,
    hideTooltip,
    updateTooltip,
    tooltipElement,
  } = useFloatingTooltip(tooltipMessage);

  const [readOnly, setReadOnly] = useState(clientReadOnly);
  const [plannerReadOnly, setPlannerReadOnly] = useState(plannerOnly);

  const setPermissions = (permissionsReadOnly, permissionsPlannerReadOnly) => {
    setReadOnly(permissionsReadOnly);
    setPlannerReadOnly(permissionsPlannerReadOnly);
  };

  const isInOptionMenu = (card, element) => {
    if (card === element || !element.parentElement) return false;
    if (element.classList.contains('ap-dropdown__content') ||
      element.classList.contains('ReactModal__Overlay') ||
      element.classList.contains('drawer-container') ||
      element.classList.contains('note-item__options')) {
        return true;
    }
    return isInOptionMenu(card, element.parentElement);
  };

  const dragListeners = !editNoteTitle && isBrowser ? listeners : null;

  return (
    <div
      key={`status-${id}`}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...dragListeners}
    >
      <div
        ref={cardRef}
        className={clsx('note-item', !imageUrl && 'no-image')}
        onClick={(e) => {
          if (editNoteTitle || isInOptionMenu(cardRef.current, e.target)) {
            return;
          }
          goToNote(id, url);
        }}
      >
        <div className="note-item__avatar">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={title}
              onDragStart={(e) => {
                e.preventDefault();
              }}
            />
          ) : null}
        </div>
        {IsImageUpdating && <span className="note-item__spinner fa fa-spin fa-spinner" />}
        <div className="note-item__name">
          {
            editNoteTitle ? (
              <AutosizeTextarea
                className={notesViewOptions.view === 'list' ?
                  'ap-text note-item__name_update_list unstyled' :
                  'ap-text note-item__name_update_grid unstyled'}
                defaultValue={noteTitle.length <= MAX_NOTE_NAME_LENGTH
                  ? noteTitle
                  : `${noteTitle.slice(0, MAX_NOTE_NAME_LENGTH)}`}
                onBlur={onChangeTitle}
                autoFocus
                onFocus={(e) => {
                  e.target.selectionStart = e.target.value.length;
                }}
              />
            )
              : (
                <div className='note-item__name-text'>
                  {noteTitle.length <= MAX_NOTE_NAME_LENGTH
                    ? noteTitle
                    : `${noteTitle.slice(0, MAX_NOTE_NAME_LENGTH)}...`}
                  {(isOwner || !clientReadOnly) && notesEdit  ? (
                    <button
                      className='note-item__name-icon button--unstyled'
                      onClick={(e) => {
                        setEditNoteTitle(true);
                        e.stopPropagation();
                      }}
                    >
                      <FontAwesomeIcon
                        icon={['fas', 'pencil']}
                      />
                    </button>
                  ) : null}
                </div>
              )
          }
        </div>
        {url && (
        <a
          href={url}
          className="note-item__link"
          target="_blank"
          rel="noopener noreferrer"
        >
          <OpenInNewIcon />
        </a>
        )}
        <div className="note-item__permissions">
          {plannerReadOnly  && (
            <div className="note-item__permissions-item">
              <FontAwesomeIcon icon={['fas', 'eye-slash']}
                onMouseEnter={e => {
                  showTooltip();
                  updateTooltip(e);
                  setTooltipMessage('Visible to org members only');
                }}
                onMouseMove={e => updateTooltip(e)}
                onMouseLeave={hideTooltip}
              />
              <span className="note-item__permissions-text">
                Visible to org members only
              </span>
            </div>
          )}
          {readOnly && (
            <div className="note-item__permissions-item">
              <FontAwesomeIcon icon={['fas', 'lock-alt']}
                onMouseEnter={e => {
                  showTooltip();
                  updateTooltip(e);
                  setTooltipMessage('Client editing locked');
                }}
                onMouseMove={e => updateTooltip(e)}
                onMouseLeave={hideTooltip}
              />
              <span className="note-item__permissions-text">
                Client editing locked
              </span>
            </div>
          )}
        </div>
        <div className="note-item__options">
          <NotesOptionsButton
            {...restProps}
            imageUrls={imageUrls}
            image={image}
            originalImage={originalImage}
            id={id}
            url={url}
            note={noteData}
            renameAction={()=>setEditNoteTitle(true)}
            setImageUpdating={setImageUpdating}
            setPermissions={setPermissions}
            isOwner={isOwner}
            notesEdit={notesEdit}
          />
        </div>
        {notesEdit && (
          <div className="note-item__dnd-icon">
            <ZoomOutMapIcon />
          </div>
        )}
        {tooltipElement}
      </div>
    </div>
  );
};

export default Note;
